import React, { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Search, Check, Plus, X, Loader2 } from "lucide-react";
import { loadMetiers, matchMetier, metierIcon } from "./metierUtils";

/**
 * Sélecteur de modules métier (PBM-METIER-…) : recherche + grille d'icônes.
 * value = code principal ; secondaires = codes secondaires (facultatif, si onSecondairesChange est fourni).
 */
export const MetierPicker = ({ value, onChange, secondaires = [], onSecondairesChange, lockPrincipal = false, compact = false, testid = "metier-picker" }) => {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { loadMetiers().then(setItems).catch(() => setItems([])); }, []);
  const shown = useMemo(() => (items || []).filter((m) => matchMetier(m, q)), [items, q]);
  const principal = (items || []).find((m) => m.code === value);
  const withSecondaires = typeof onSecondairesChange === "function";

  const toggleSecondaire = (code) => {
    if (code === value) return;
    onSecondairesChange(secondaires.includes(code) ? secondaires.filter((c) => c !== code) : [...secondaires, code].slice(0, 6));
  };
  const pick = (m) => {
    if (lockPrincipal && value) { if (withSecondaires) toggleSecondaire(m.code); return; }
    if (value === m.code) return;
    onChange(m.code, m);
    if (withSecondaires && secondaires.includes(m.code)) onSecondairesChange(secondaires.filter((c) => c !== m.code));
  };

  if (items === null) return <div className="text-xs text-[#868E96] flex items-center gap-2 py-3" data-testid={`${testid}-loading`}><Loader2 className="h-4 w-4 animate-spin" />Chargement des métiers…</div>;
  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#868E96]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un métier (restaurant, garage, coiffure, BTP…)" className="h-10 pl-9" data-testid={`${testid}-search`} />
      </div>
      {principal && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid={`${testid}-selected`}>
          <span className="inline-flex items-center gap-1.5 bg-[#0A0A0A] text-[#FFD700] px-2.5 py-1 font-bold uppercase tracking-wider text-[10px]"><Check className="h-3 w-3" />{principal.label}</span>
          {withSecondaires && secondaires.map((c) => { const m = items.find((x) => x.code === c); return m ? <button type="button" key={c} onClick={() => toggleSecondaire(c)} className="inline-flex items-center gap-1 border border-[#0A0A0A] bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-wider hover:bg-[#F8F9FA]" data-testid={`${testid}-secondaire-${c}`}>{m.label}<X className="h-3 w-3" /></button> : null; })}
          {withSecondaires && <span className="text-[10px] text-[#868E96]">{secondaires.length ? "Cliquez pour retirer un métier secondaire." : "Ajoutez vos métiers secondaires avec le bouton « + »."}</span>}
        </div>
      )}
      <div className={`grid gap-2 ${compact ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"} max-h-72 overflow-y-auto pr-1`} role="listbox" data-testid={`${testid}-grid`}>
        {shown.map((m) => {
          const Icon = metierIcon(m.icon);
          const isP = m.code === value, isS = secondaires.includes(m.code);
          return (
            <div key={m.code} className={`relative border text-left transition-colors ${isP ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : isS ? "border-[#002FA7] bg-[#F5F7FF]" : "border-[#DEE2E6] bg-white hover:border-[#0A0A0A]"}`}>
              <button type="button" role="option" aria-selected={isP} onClick={() => pick(m)} className="w-full text-left p-3 pr-8" data-testid={`${testid}-item-${m.code}`} title={m.audience}>
                <Icon className={`h-5 w-5 ${isP ? "text-[#FFD700]" : "text-[#002FA7]"}`} />
                <div className="text-xs font-bold leading-tight mt-2 line-clamp-2">{m.label}</div>
                <div className={`text-[10px] mt-0.5 line-clamp-1 ${isP ? "text-white/60" : "text-[#868E96]"}`}>{m.audience}</div>
              </button>
              {withSecondaires && !isP && value && (
                <button type="button" onClick={() => toggleSecondaire(m.code)} aria-pressed={isS} title={isS ? "Retirer des métiers secondaires" : "Ajouter en métier secondaire"} data-testid={`${testid}-toggle-${m.code}`}
                  className={`absolute top-2 right-2 h-6 w-6 flex items-center justify-center border text-[10px] ${isS ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-[#DEE2E6] hover:border-[#002FA7]"}`}>{isS ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}</button>
              )}
            </div>
          );
        })}
        {!shown.length && <div className="col-span-full text-xs text-[#868E96] py-6 text-center" data-testid={`${testid}-empty`}>Aucun métier ne correspond à « {q} ».</div>}
      </div>
    </div>
  );
};
